import * as React from "react";
import cs from "classnames";
import { NegativeRegion, Region, RegionType } from "../../api/StudyApi";
import arr from "../../util/ArrayDecorator";
import { CodeLabel } from "../../api/GrammarApi";
import i18n from "i18next";

export const HARD_POSITIVE_CONFIDENCE = 200;
export const POSITIVE_CONFIDENCE = 100;
export const LOW_POSITIVE_CONFIDENCE = 50;
export const DARK_ZONE_CONFIDENCE = 0;

export const HARD_NEGATIVE_CONFIDENCE = 200;
export const NEGATIVE_CONFIDENCE = 100;
export const DOUBTFUL_NEGATIVE_CONFIDENCE = 0;

const KEYCODE_SHIFT_LEFT = 16;
const KEYCODE_DEL = 8;

interface RegionMenuProps {
  region: Region;

  onChange(region: Region): void;

  onDelete(region: Region): void;

  onMouseLeave(): void;

  onKeyDown(evt: React.KeyboardEvent): void;
}

type ConfidenceButtonProps = {
  confidence: number;
  title: string;
  icon: number;
  region: Region;
  onChange: (confidence: number) => void;
};

function ConfidenceButton({ confidence, title, icon, region, onChange }: ConfidenceButtonProps) {
  const className = cs("btn btn-light text-left", { active: region.confidence === confidence });

  return (
    <button type="button" className={className} onClick={() => onChange(confidence)} title={title}>
      <i className={"fa fa-thermometer-" + icon} /> {title}
    </button>
  );
}

export default class RegionMenu extends React.Component<RegionMenuProps> {
  onChangeConfidence = (val: number) => {
    const region = this.props.region;

    if (region.confidence !== val) {
      region.confidence = val;
      this.props.onChange(region);
    }
  };

  buttonClass(confidence: number) {
    let clss = "btn btn-light text-left";

    if (confidence === this.props.region.confidence) {
      clss += " active";
    }

    return clss;
  }

  private onKeyDown(evt: React.KeyboardEvent) {
    if (evt.keyCode === KEYCODE_DEL) {
      this.props.onDelete(this.props.region);
    }

    if (evt.keyCode === KEYCODE_SHIFT_LEFT) {
      if (this.props.region._type === RegionType.OBSERVATION) {
        this.onChangeConfidence(POSITIVE_CONFIDENCE);
      } else if (this.props.region._type === RegionType.NEGATIVE) {
        this.onChangeConfidence(HARD_NEGATIVE_CONFIDENCE);
      }
    }

    if (evt.ctrlKey) {
      if (this.props.region._type === RegionType.OBSERVATION) {
        this.onChangeConfidence(DARK_ZONE_CONFIDENCE);
      } else if (this.props.region._type === RegionType.NEGATIVE) {
        this.onChangeConfidence(DOUBTFUL_NEGATIVE_CONFIDENCE);
      }
    }

    this.props.onKeyDown(evt);
  }

  private renderTag(option: CodeLabel) {
    let clazz = "badge cursor-pointer";

    if (arr((this.props.region as NegativeRegion).tags).containsElement(option.code)) {
      clazz += " badge-light";
    } else {
      clazz += " badge-dark";
    }

    const toggleTag = (evt: React.MouseEvent, code: string) => {
      const negRegion = this.props.region as NegativeRegion;
      if (!negRegion.tags) negRegion.tags = [];

      const tagsArr = arr(negRegion.tags);
      if (tagsArr.containsElement(code)) {
        tagsArr.remove((elt) => elt === code);
      } else {
        tagsArr.push(code);
      }
      this.props.onChange(negRegion);
    };

    return (
      <span key={option.code} className={clazz} onClick={(evt) => toggleTag(evt, option.code)}>
        {option.label}
      </span>
    );
  }

  render() {
    return (
      <div
        className="btn-group-vertical"
        onMouseLeave={this.props.onMouseLeave}
        /* Remove outline because element is focus, in order to activate onKeyDown -> https://stackoverflow.com/questions/47155448/how-to-handle-onkeydown-event-on-div-in-reactjs */
        style={{ outline: "none" }}
        tabIndex={0}
        ref={(c) => c && c.focus()}
        onKeyDown={(evt) => this.onKeyDown(evt)}
      >
        <button
          type="button"
          className="btn btn-light text-left"
          onClick={(evt) => this.props.onDelete(this.props.region)}
        >
          <i className="fa fa-trash" /> {i18n.t("global.delete") + " ..."}
        </button>

        {this.props.region._type === RegionType.OBSERVATION && (
          <div>
            <ConfidenceButton
              confidence={HARD_POSITIVE_CONFIDENCE}
              title={i18n.t("global.visibleHp")}
              icon={4}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
            <ConfidenceButton
              confidence={POSITIVE_CONFIDENCE}
              title={i18n.t("global.visible")}
              icon={3}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
            <ConfidenceButton
              confidence={LOW_POSITIVE_CONFIDENCE}
              title={i18n.t("global.lessVisible")}
              icon={1}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
            <ConfidenceButton
              confidence={DARK_ZONE_CONFIDENCE}
              title={i18n.t("global.blackZone")}
              icon={0}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
          </div>
        )}

        {this.props.region._type === RegionType.NEGATIVE && (
          <div>
            <ConfidenceButton
              confidence={HARD_NEGATIVE_CONFIDENCE}
              title={i18n.t("global.hardNegative")}
              icon={4}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
            <ConfidenceButton
              confidence={NEGATIVE_CONFIDENCE}
              title={i18n.t("global.negative")}
              icon={2}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
            <ConfidenceButton
              confidence={DOUBTFUL_NEGATIVE_CONFIDENCE}
              title={i18n.t("global.uncertainZone")}
              icon={0}
              onChange={this.onChangeConfidence}
              region={this.props.region}
            />
            {this.props.region.availableTags && this.props.region.availableTags.map((option) => this.renderTag(option))}
          </div>
        )}
      </div>
    );
  }
}
